/** Helpers for starting crawls while Crawl4AI may still be cold-starting. */

const CRAWL_START_MAX_ATTEMPTS = 3;
const CRAWL_START_RETRY_PAUSE_MS = 2500;

const FINAL_CRAWL_START_ERROR =
  'Crawler is taking too long to start. Please try again later.';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function extractErrorDetail(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === 'string' ? item : JSON.stringify(item)))
      .join('; ');
  }
  const message = (error as { message?: string })?.message;
  return typeof message === 'string' ? message : '';
}

/** True when the failure is likely a cold Crawl4AI / proxy timeout we should auto-retry. */
export function isRetryableCrawlStartError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 503 || status === 504 || status === 502) return true;

  const detail = extractErrorDetail(error).toLowerCase();
  if (detail.includes('crawl4ai autoscaling') || detail.includes('timed out waiting for ecs')) {
    return true;
  }

  // Axios / browser network failures (e.g. ALB idle timeout mid-wait).
  const code = (error as { code?: string })?.code;
  if (!status && (code === 'ECONNABORTED' || code === 'ERR_NETWORK')) return true;
  if (!status && detail.includes('network error')) return true;

  return false;
}

export type CrawlStartRetryHandlers = {
  /** Called before attempt 2+ so the UI can explain an automatic retry. */
  onRetry?: (attempt: number, maxAttempts: number) => void;
};

/**
 * Runs a crawl-start API call, automatically retrying transient crawler warm-up failures.
 * The user does not need to click again; callers should keep a busy state until this settles.
 */
export async function withCrawlStartRetries<T>(
  action: () => Promise<T>,
  handlers: CrawlStartRetryHandlers = {},
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= CRAWL_START_MAX_ATTEMPTS; attempt++) {
    try {
      return await action();
    } catch (error) {
      lastError = error;
      const canRetry =
        attempt < CRAWL_START_MAX_ATTEMPTS && isRetryableCrawlStartError(error);
      if (!canRetry) {
        break;
      }
      handlers.onRetry?.(attempt + 1, CRAWL_START_MAX_ATTEMPTS);
      await sleep(CRAWL_START_RETRY_PAUSE_MS);
    }
  }

  if (isRetryableCrawlStartError(lastError)) {
    throw new Error(FINAL_CRAWL_START_ERROR);
  }
  throw lastError;
}

export function crawlStartFailureMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message === FINAL_CRAWL_START_ERROR) {
    return FINAL_CRAWL_START_ERROR;
  }
  const detail = extractErrorDetail(error);
  if (isRetryableCrawlStartError(error)) {
    return FINAL_CRAWL_START_ERROR;
  }
  return detail || fallback;
}
